import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import InputGroup from 'react-bootstrap/InputGroup';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { PageId } from '@app/types';
import { getProgress, requestSaveWord, requestSubtitlesAutocomplete, requestSubtitlesWords, requestYoutubeWords, saveProgress } from '@app/utils/api';
import { useDebounceCallback } from '@app/utils/useDebounceCallback';
import { AutocompleteEpisode, AutocompleteItem, AutocompleteSeason, MediaType } from '@shared/types';
import { getWordsDataAtomDefaults, wordsChunkLoadingAtom, wordsDataAtom, wordsErrorAtom, wordsLoadingAtom, wordsLoadMoreAtom, wordToSaveAtom } from '@app/atoms/wordsAtoms';
import { loadingProgressItemAtom, loadingShowIdsAtom, localLoadingProgressItemAtom, localLoadingShowIdsAtom } from '@app/atoms/uiAtoms';
import './SearchPanel.scss'
import MediaInfo from '@app/components/MediaInfo';
import MediaSelector from '@app/components/MediaSelector';
import { selectedEpisodeAtom, selectedMediaAtom, selectedMediaInfoAtom, selectedSeasonAtom } from '@app/atoms/selectedMediaAtoms';
import AutocompleteDropdown from '@app/components/AutocompleteDropdown';
import { PhrasesResponse, WordData } from '@shared/types/words';
import { buildShowIdentifier, collectShowIdentifiers, isYoutubeShow } from '@shared/utils/show';

interface SearchPanelProps {
  pageId: PageId;
  onSelectMedia?: (media: AutocompleteItem) => void;
  forceSeasonPicker?: boolean;
}

const SearchPanel = ({ pageId, onSelectMedia, forceSeasonPicker = false }: SearchPanelProps) => {
  const navigate = useNavigate();
  const { showKey } = useParams();
  const routeShowKey = showKey ? decodeURIComponent(showKey) : '';
  const [autocomplete, setAutocomplete] = useState<AutocompleteItem[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState<boolean>(false);
  const [mediaSelectorActive, setMediaSelectorActive] = useState<boolean>(false);

  const [selectedMedia, setSelectedMedia] = useAtom(selectedMediaAtom);
  const [selectedSeason, setSelectedSeason] = useAtom(selectedSeasonAtom);
  const [selectedEpisode, setSelectedEpisode] = useAtom(selectedEpisodeAtom);
  const [wordsData, setWordsData] = useAtom(wordsDataAtom);
  const shortInfo = useAtomValue(selectedMediaInfoAtom);
  const [wordsLoading, setWordsLoading] = useAtom(wordsLoadingAtom);
  const [wordsChunkLoading, setWordsChunkLoading] = useAtom(wordsChunkLoadingAtom);
  const setWordsError = useSetAtom(wordsErrorAtom);
  const loadingShowIds = useAtomValue(loadingShowIdsAtom);
  const localLoadingShowIds = useAtomValue(localLoadingShowIdsAtom);
  const loadingProgressItem = useAtomValue(loadingProgressItemAtom);
  const localLoadingProgressItem = useAtomValue(localLoadingProgressItemAtom);
  const setLocalLoadingShowIds = useSetAtom(localLoadingShowIdsAtom);
  const setLocalLoadingProgressItem = useSetAtom(localLoadingProgressItemAtom);
  const wordsLoadMore = useAtomValue(wordsLoadMoreAtom);
  const [wordToSave, setWordToSave] = useAtom(wordToSaveAtom);

  const loadMoreRef = useRef<number>(wordsLoadMore);
  const wordToSaveRef = useRef<WordData | null>(wordToSave);
  const showNameRef = useRef<HTMLInputElement | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeLoadingElapsedSec, setYoutubeLoadingElapsedSec] = useState(0);
  const [youtubeLoadingMode, setYoutubeLoadingMode] = useState<'extract' | 'restore'>('extract');
  const [standardLoadingElapsedSec, setStandardLoadingElapsedSec] = useState(0);
  const [standardLoadingMode, setStandardLoadingMode] = useState<'extract' | 'load_more' | 'restore'>('extract');
  const loadingTips = [
    'Tip: focus on phrases, not single words.',
    'Tip: say each phrase aloud once before flipping.',
    'Tip: mark hard cards for review later.'
  ];

  const wordsReqIdRef = useRef(0);
  const lastLoadedKeyRef = useRef<string>('');
  const forcedPickerOpenedRef = useRef<string>('');
  const LOADING_LOCK_ERROR = 'Another material is still loading. Please wait until it finishes.';
  const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const getLoadingHint = (elapsedSec: number) => loadingTips[Math.floor(elapsedSec / 6) % loadingTips.length];
  const getLoadingTitle = (mode: 'extract' | 'load_more' | 'restore') => {
    if (mode === 'extract') {
      return 'Preparing your first cards...';
    }
    if (mode === 'load_more') {
      return 'Preparing more cards...';
    }
    return 'Opening your learning session...';
  };
  const getLoadingEta = (mode: 'extract' | 'load_more' | 'restore', elapsedSec: number) => {
    if (mode === 'extract') {
      return `Usually takes 10-20 seconds. Elapsed: ${elapsedSec}s.`;
    }
    if (mode === 'load_more') {
      return `Usually takes 5-12 seconds. Elapsed: ${elapsedSec}s.`;
    }
    return `Usually takes a few seconds. Elapsed: ${elapsedSec}s.`;
  };
  const isProcessingError = (response: PhrasesResponse, isYoutubeMode = false) => {
    if (!response?.error) {
      return false;
    }
    const message = response.error.toLowerCase();
    if (message === 'processing') {
      return true;
    }
    if (message.includes('wordbatch.status') || message.includes('processing')) {
      return true;
    }
    return !isYoutubeMode && message.includes('failed to fetch or download subtitles');
  };

  const buildWordsState = (
    response: PhrasesResponse,
    words: WordData[] = response.words,
    parts?: Map<number, PhrasesResponse>
  ): ReturnType<typeof getWordsDataAtomDefaults> => ({
    words,
    parts: parts || new Map<number, PhrasesResponse>([[response.part, { ...response, words }]]),
    totalParts: response.totalParts,
    nextPartToLoad: response.nextPart || (response.part + 1),
    previewPending: !!response.previewPending,
    previewTargetSize: response.previewTargetSize || 0,
    previewLoadedSize: words.length,
  });

  const hydratePreviewRemainder = async ({
    reqId,
    response,
    media,
    season,
    episode,
    isYoutubeMode
  }: {
    reqId: number;
    response: PhrasesResponse;
    media: AutocompleteItem;
    season?: number | null;
    episode?: number | null;
    isYoutubeMode: boolean;
  }) => {
    if (!response.previewPending || !response.previewRemainderPart) {
      return;
    }

    const remainderPart = response.previewRemainderPart;
    const maxAttempts = 240;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (reqId !== wordsReqIdRef.current) {
        return;
      }

      const remainder = isYoutubeMode
        ? await requestYoutubeWords({
          screenmediaId: media.dbId,
          part: remainderPart
        })
        : await requestSubtitlesWords({
          show: { ...media },
          season: season ?? undefined,
          episode: episode ?? undefined,
          part: remainderPart
        });

      if (reqId !== wordsReqIdRef.current) {
        return;
      }

      if (!remainder.error && remainder.part === remainderPart && remainder.words.length) {
        const mergedWords = response.words.concat(remainder.words);
        const parts = new Map<number, PhrasesResponse>();
        parts.set(1, { ...response, words: mergedWords, previewPending: false, nextPart: remainder.nextPart || (remainderPart + 1) });
        parts.set(remainderPart, remainder);

        setWordsData({
          words: mergedWords,
          parts,
          totalParts: response.totalParts,
          nextPartToLoad: remainder.nextPart || (remainderPart + 1),
          previewPending: false,
          previewTargetSize: response.previewTargetSize || 0,
          previewLoadedSize: mergedWords.length,
        });
        await saveProgress({
          show: media,
          season: season ?? undefined,
          episode: episode ?? undefined,
          screenmediaId: media.dbId,
          details: {
            loadedCardsTotal: response.previewTargetSize || mergedWords.length,
          }
        });
        return;
      }

      await wait(2000);
    }
  };

  const resetSelectedContext = () => {
    // Invalidate pending words requests to avoid stale data reappearing.
    wordsReqIdRef.current += 1;
    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');
    setWordsLoading(false);
    setWordsChunkLoading(false);
    setMediaSelectorActive(false);
    lastLoadedKeyRef.current = '';
  };

  const isLoadLockedForShow = (target: AutocompleteItem | string | null | undefined): boolean => {
    const activeIds = new Set<string>([...loadingShowIds, ...localLoadingShowIds]);
    if (!activeIds.size) {
      return false;
    }
    const ids = typeof target === 'string'
      ? [target, routeShowKey]
      : collectShowIdentifiers(target, [routeShowKey]);
    if (!ids.length) {
      return true;
    }
    return !ids.some((id) => activeIds.has(id));
  };
  const isGlobalLoadLockActive = loadingShowIds.size > 0 || localLoadingShowIds.size > 0;

  const startLocalLoading = ({
    media,
    season,
    episode,
    screenmediaId,
  }: {
    media: AutocompleteItem;
    season?: number;
    episode?: number;
    screenmediaId?: string;
  }) => {
    const ids = collectShowIdentifiers(media, [screenmediaId, routeShowKey]);
    setLocalLoadingShowIds(new Set(ids));
    setLocalLoadingProgressItem({
      id: `local-loading:${Date.now()}`,
      show: media,
      screenmediaId,
      season,
      episode,
      updatedAt: new Date(),
      lastUsedAt: new Date(),
      dataStatus: 'processing' as any,
      wordbatches: [],
      details: {},
    });
  };

  const stopLocalLoading = () => {
    setLocalLoadingShowIds(new Set());
    setLocalLoadingProgressItem(null);
  };

  const activeLoadingItem = loadingProgressItem || localLoadingProgressItem;
  const loadingItemTitle = (() => {
    const raw = `${activeLoadingItem?.show?.originalTitle || activeLoadingItem?.show?.title || ''}`.trim();
    if (!raw) {
      return 'another material';
    }
    return raw;
  })();
  const loadingItemEpisodeTag = (() => {
    if (!activeLoadingItem) {
      return '';
    }
    if (typeof activeLoadingItem.season === 'number' && typeof activeLoadingItem.episode === 'number') {
      return `S${String(activeLoadingItem.season).padStart(2, '0')}E${String(activeLoadingItem.episode).padStart(2, '0')}`;
    }
    return '';
  })();
  const openCurrentLoadingItem = () => {
    if (!activeLoadingItem?.show) {
      return;
    }
    const targetShowKey = buildShowIdentifier(activeLoadingItem.show)
      || activeLoadingItem.screenmediaId
      || '';
    if (!targetShowKey) {
      return;
    }
    const isYoutube = isYoutubeShow(activeLoadingItem.show);
    const targetPage = isYoutube
      ? PageId.YOUTUBE
      : activeLoadingItem.show.type === MediaType.TVSHOW
        ? PageId.SERIES
        : PageId.MOVIES;
    navigate(`/${targetPage}/${encodeURIComponent(targetShowKey)}`);
  };
  const effectiveStandardLoadingMode: 'extract' | 'load_more' | 'restore' = (
    standardLoadingMode === 'restore' && wordsData.words.length === 0
      ? 'extract'
      : standardLoadingMode
  );

  const canRestoreExistingProgressWhileLocked = async ({
    media,
    season,
    episode,
    isYoutubeMode = false,
  }: {
    media: AutocompleteItem;
    season?: number;
    episode?: number;
    isYoutubeMode?: boolean;
  }): Promise<boolean> => {
    const hasRestorableData = (item: any): boolean => {
      if (!item) {
        return false;
      }
      const loadedCardsTotal = Number(item.details?.loadedCardsTotal) || 0;
      const hasStatuses = Object.keys(item.details?.cardStatuses || {}).length > 0;
      const hasBatches = (item.wordbatches || []).length > 0;
      return loadedCardsTotal > 0 || hasStatuses || hasBatches;
    };

    try {
      if (isYoutubeMode && media.dbId) {
        const byScreenmedia = await getProgress(1, 1, {
          screenmediaId: media.dbId,
          season,
          episode,
          includeHidden: true,
        });
        if (hasRestorableData(byScreenmedia.list?.[0])) {
          return true;
        }
      }

      const showId = buildShowIdentifier(media);
      if (!showId) {
        return false;
      }
      const byShowId = await getProgress(1, 1, {
        showId,
        season,
        episode,
        includeHidden: true,
      });
      return hasRestorableData(byShowId.list?.[0]);
    } catch {
      return false;
    }
  };

  const parseYoutubeVideoId = (url: string): string => {
    try {
      const normalized = new URL(url.trim());
      if (normalized.hostname.includes('youtu.be')) {
        return normalized.pathname.replace(/^\/+/, '').slice(0, 11);
      }
      if (normalized.hostname.includes('youtube.com')) {
        const fromQuery = normalized.searchParams.get('v');
        if (fromQuery) {
          return fromQuery.slice(0, 11);
        }
        const parts = normalized.pathname.split('/').filter(Boolean);
        if (parts[0] === 'shorts' || parts[0] === 'embed') {
          return parts[1]?.slice(0, 11) || '';
        }
      }
    } catch {
      return '';
    }
    return '';
  };

  const sectionTitle = pageId === PageId.YOUTUBE
    ? 'Learn from YouTube'
    : pageId === PageId.MOVIES
      ? 'Learn from Movies'
      : 'Learn from TV Shows';
  const sectionHint = pageId === PageId.YOUTUBE
    ? 'Paste a YouTube link and extract practical vocabulary.'
    : pageId === PageId.MOVIES
      ? 'Search a movie and extract words from its subtitles.'
      : 'Search a show, pick season and episode, then extract words.';
  const searchPlaceholder = pageId === PageId.MOVIES
    ? 'Type a movie title...'
    : 'Type a TV show title...';

  const handleSearchChange = useDebounceCallback(async (queryRaw: string) => {
    // if (value.length < 3) {
    //   return;
    // }
    const query = queryRaw.trim();
    if (!query.length) {
      if (autocomplete.length) {
        setAutocomplete([]);
      }
      setAutocompleteLoading(false);

      return;
    }

    // Starting a new search must clear previous media/progress context
    // so the user does not see stale progress while picking another title.
    if (selectedMedia || selectedSeason || selectedEpisode || wordsData.words.length) {
      resetSelectedContext();
    }

    try {
      const autocompleteData = await requestSubtitlesAutocomplete(pageId, query);
      setAutocomplete(autocompleteData);
    } finally {
      setAutocompleteLoading(false);
    }
  }, 1000, [pageId]);

  const handleSearchSelect = (autocompleteItem: AutocompleteItem) => {
    if (isLoadLockedForShow(autocompleteItem)) {
      return;
    }

    if (onSelectMedia) {
      setAutocomplete([]);
      if (showNameRef.current?.value) {
        showNameRef.current.value = '';
      }
      onSelectMedia(autocompleteItem);
      return;
    }

    setSelectedMedia(autocompleteItem);
    if (autocompleteItem.seasons?.length) {
      setSelectedSeason(autocompleteItem.seasons[0]);
      setSelectedEpisode(null);
    } else {
      setSelectedSeason(null);
    }
    setAutocomplete([]);
    if (showNameRef.current?.value) {
      showNameRef.current.value = '';
    }

    setMediaSelectorActive(true);
  };

  const handleMediaSelectorExit = () => setMediaSelectorActive(false);
  const handleMediaSelectorOpenSafe = () => {
    if (isLoadLockedForShow(selectedMedia)) {
      return;
    }
    setMediaSelectorActive(true);
  };

 const loadWords = async ({ season, episode, trigger = 'manual' }: { season: AutocompleteSeason | null, episode: AutocompleteEpisode | null, trigger?: 'manual' | 'auto' }) => {
    if (!selectedMedia) {
      return;
    }
    if (isLoadLockedForShow(selectedMedia)) {
      if (trigger !== 'auto') {
        return;
      }
      const canRestore = await canRestoreExistingProgressWhileLocked({
        media: selectedMedia,
        season: season?.num,
        episode: episode?.num,
      });
      if (!canRestore) {
        return;
      }
    }

    const mediaAny = selectedMedia as any;
    const mediaId = String(mediaAny.imdbId ?? mediaAny.id ?? mediaAny.value ?? mediaAny.title ?? '');
    const key = `${pageId}|${selectedMedia.type}|${mediaId}|${season?.num ?? ''}|${episode?.num ?? ''}`;

    // If we already have data for this exact key - skip
    if (lastLoadedKeyRef.current === key && (wordsLoading || wordsData.words.length)) {
      return;
    }

    const reqId = ++wordsReqIdRef.current;
    lastLoadedKeyRef.current = key;

    setWordsError('');
    setWordsData(getWordsDataAtomDefaults());
    setSelectedSeason(season);
    setSelectedEpisode(episode);

    setWordsLoading(true);
    setStandardLoadingMode(trigger === 'auto' ? 'restore' : 'extract');
    startLocalLoading({
      media: selectedMedia,
      season: season?.num,
      episode: episode?.num,
      screenmediaId: selectedMedia.dbId,
    });

    try {
      saveProgress({
        show: selectedMedia,
        season: season?.num,
        episode: episode?.num,
        screenmediaId: selectedMedia.dbId
      });

      let response: PhrasesResponse = await requestSubtitlesWords({
        show: { ...selectedMedia },
        season: season?.num,
        episode: episode?.num,
        part: 1
      });

      // Keep primary loader alive while backend is still preparing batches.
      // Short caps here cause a "blank" state on return to series.
      const maxAttempts = trigger === 'auto' ? 600 : 400;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (reqId !== wordsReqIdRef.current) {
          return;
        }
        if (!isProcessingError(response)) {
          break;
        }
        await wait(1500);
        response = await requestSubtitlesWords({
          show: { ...selectedMedia },
          season: season?.num,
          episode: episode?.num,
          part: 1
        });
      }
  
      // Ignore stale response
      if (reqId !== wordsReqIdRef.current) {
        return;
      }

      if (!response.error && response.part === 1) {
        if (reqId === wordsReqIdRef.current) {
          setWordsData(buildWordsState(response));
          await saveProgress({
            show: selectedMedia,
            season: season?.num,
            episode: episode?.num,
            screenmediaId: selectedMedia.dbId,
            details: {
              loadedCardsTotal: response.previewTargetSize || response.words.length,
            }
          });

          void hydratePreviewRemainder({
            reqId,
            response,
            media: selectedMedia,
            season: season?.num,
            episode: episode?.num,
            isYoutubeMode: false
          });
        }
  
        // TODO: disable "Get Important Words" button till changes
        // setGetWordsDisabled(true);
      } else if (!isProcessingError(response)) {
        setWordsError(response.error || 'Incorrect words response');
      }

      // setVisible(false);
    } finally {
      // Only the latest request can stop loading
      if (reqId === wordsReqIdRef.current) {
        setWordsLoading(false);
        stopLocalLoading();
      }
    }
  };

  const loadMoreWords = async ({
    part,
    media,
    extendActiveCardsLimit = true,
    showPreparingError = true,
    maxAttemptsOverride,
  }: {
    part: number,
    media: AutocompleteItem,
    extendActiveCardsLimit?: boolean,
    showPreparingError?: boolean,
    maxAttemptsOverride?: number,
  }) => {
    if (isLoadLockedForShow(media)) {
      return null;
    }
    const reqId = ++wordsReqIdRef.current;
    const usePrimaryLoading = wordsData.words.length === 0;
    if (usePrimaryLoading) {
      setWordsLoading(true);
    } else {
      setWordsChunkLoading(true);
    }
    startLocalLoading({
      media,
      season: selectedSeason?.num,
      episode: selectedEpisode?.num,
      screenmediaId: media.dbId,
    });
    setWordsError('');
    try {
      const isYoutubeMode = pageId === PageId.YOUTUBE;
      if (isYoutubeMode) {
        setYoutubeLoadingMode('restore');
      } else {
        setStandardLoadingMode('load_more');
      }

      let response: PhrasesResponse | null = null;
      const maxAttempts = maxAttemptsOverride ?? (isYoutubeMode ? 40 : 80);
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const candidate: PhrasesResponse = isYoutubeMode
          ? await requestYoutubeWords({
            screenmediaId: media.dbId,
            part
          })
          : await requestSubtitlesWords({
            show: { ...media },
            season: selectedSeason?.num,
            episode: selectedEpisode?.num,
            part
          });

        if (reqId !== wordsReqIdRef.current) {
          return null;
        }

        const isBackendStillPreparing = !isYoutubeMode
          && isProcessingError(candidate, isYoutubeMode);
        response = candidate;
        if (!isBackendStillPreparing || attempt === maxAttempts - 1) {
          break;
        }
        await wait(1500);
      }

      if (reqId !== wordsReqIdRef.current) {
        return null;
      }

      if (!response) {
        return null;
      }

      if (!response.error && response.words.length && response.part) {
        const resolvedResponse = response;
        let mergedWordsLength = 0;
        let hasNewPart = false;
        setWordsData((prev) => {
          if (prev.parts.has(resolvedResponse.part)) {
            return {
              ...prev,
              nextPartToLoad: resolvedResponse.nextPart || (resolvedResponse.part + 1),
              previewPending: false,
            };
          }

          const nextParts = new Map(prev.parts);
          nextParts.set(resolvedResponse.part, resolvedResponse);

          const prevWords = prev.words as WordData[];
          const existingWordIds = new Set(prevWords.map((word) => word.id));
          const newWords = resolvedResponse.words.filter((word) => !existingWordIds.has(word.id));
          const mergedWords = prevWords.concat(newWords);

          mergedWordsLength = mergedWords.length;
          hasNewPart = true;

          return {
            words: mergedWords,
            parts: nextParts,
            totalParts: resolvedResponse.totalParts || prev.totalParts,
            nextPartToLoad: resolvedResponse.nextPart || (resolvedResponse.part + 1),
            previewPending: false,
            previewTargetSize: prev.previewTargetSize,
            previewLoadedSize: mergedWords.length,
          };
        });

        if (hasNewPart && mergedWordsLength > 0) {
          const details: { loadedCardsTotal: number; activeCardsLimit?: number } = {
            loadedCardsTotal: mergedWordsLength,
          };
          if (extendActiveCardsLimit) {
            details.activeCardsLimit = mergedWordsLength;
          }
          await saveProgress({
            show: media,
            season: selectedSeason?.num,
            episode: selectedEpisode?.num,
            screenmediaId: media.dbId,
            details
          });
        }
        return response.nextPart || (response.part + 1);
      } else if (!response.error) {
        // If API returns no new words for this part, move pointer forward
        // to avoid showing "Load more" forever for an exhausted deck.
        setWordsData((prev) => ({
          ...prev,
          nextPartToLoad: part + 1,
        }));
        return part + 1;
      } else if (isProcessingError(response, isYoutubeMode)) {
        if (showPreparingError) {
          setWordsError('Still preparing the next chunk. Please wait a bit and try again.');
        }
        return null;
      } else {
        if (showPreparingError) {
          setWordsError('Next words are still preparing. Please try again in a few seconds.');
        }
        return null;
      }
    } finally {
      if (reqId === wordsReqIdRef.current) {
        if (usePrimaryLoading) {
          setWordsLoading(false);
        } else {
          setWordsChunkLoading(false);
        }
        stopLocalLoading();
      }
    }
    return null;
  };

  const loadYoutubeWords = async () => {
    if (!youtubeUrl.trim()) {
      return;
    }

    const videoId = parseYoutubeVideoId(youtubeUrl.trim());
    if (isLoadLockedForShow(videoId || undefined)) {
      return;
    }
    const reqId = ++wordsReqIdRef.current;
    setWordsError('');
    setWordsData(getWordsDataAtomDefaults());
    setWordsLoading(true);
    setYoutubeLoadingMode('extract');
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setMediaSelectorActive(false);
    const provisionalMedia: AutocompleteItem = {
      title: `yt:${videoId || ''}`,
      originalTitle: `YouTube: ${videoId || ''}`,
      year: `${new Date().getUTCFullYear()}`,
      type: 'movie',
      dbId: videoId || undefined,
    };
    startLocalLoading({
      media: provisionalMedia,
      screenmediaId: videoId || undefined,
    });

    try {
      const response: PhrasesResponse = await requestYoutubeWords({
        youtubeUrl: youtubeUrl.trim(),
        part: 1
      });

      if (response.error || response.part !== 1) {
        if (reqId !== wordsReqIdRef.current) {
          return;
        }
        setWordsError(response.error || 'Incorrect words response');
        return;
      }

      const media: AutocompleteItem = {
        title: `yt:${videoId || response.screenmediaId}`,
        originalTitle: response.mediaTitle || `Video ${videoId || ''}`.trim(),
        year: `${new Date().getUTCFullYear()}`,
        poster: response.mediaPoster,
        type: 'movie',
        dbId: response.screenmediaId
      };

      // Save resolved YouTube progress even if request became stale while user navigated away.
      await saveProgress({
        show: media,
        screenmediaId: response.screenmediaId,
        details: {
          loadedCardsTotal: response.previewTargetSize || response.words.length,
        }
      });

      if (reqId !== wordsReqIdRef.current) {
        return;
      }

      setSelectedMedia(media);
      setWordsData(buildWordsState(response));

      void hydratePreviewRemainder({
        reqId,
        response,
        media,
        isYoutubeMode: true
      });
    } finally {
      if (reqId === wordsReqIdRef.current) {
        setWordsLoading(false);
        stopLocalLoading();
      }
    }
  };

  const findWordBatch = (word: WordData): PhrasesResponse | null => {
    for (const [, part] of wordsData.parts) {
      if (part.words.some((w) => w.id === word.id)) {
        return part as PhrasesResponse;
      }
    }
    return null;
  };

  const saveWord = async(card: WordData) => {
    if (!shortInfo) {
      return;
    }

    const wordBatch = findWordBatch(card);
    
    try {
      const response = await requestSaveWord({
        wordData: card,
        metadata: shortInfo,
        wordBatchId: wordBatch?.wordBatchId,
        screenmediaId: wordBatch?.screenmediaId
      });
      console.log('Saved word response:', response);
      setWordsData((prevWordsData) => {
        const newWordsData = { ...prevWordsData };
        const word = newWordsData.words.find((w) => w.id === card.id);
        if (word) {
          word.saved = true;
        }
        return newWordsData;
      });
    } catch (err) {
      console.error('Failed to save word:', err);
    }
  };

  useEffect(() => {
    return () => {
      // Invalidate in-flight async requests on unmount.
      wordsReqIdRef.current += 1;
      lastLoadedKeyRef.current = '';
      setWordsLoading(false);
      setWordsChunkLoading(false);
      stopLocalLoading();
      setStandardLoadingElapsedSec(0);
      setYoutubeLoadingElapsedSec(0);
    };
  }, [setWordsLoading, setWordsChunkLoading]);

  useEffect(() => {
    setAutocomplete([]);
    setAutocompleteLoading(false);
    if (showNameRef?.current?.value) {
      showNameRef.current.value = '';
    }

    const selectedMediaType = selectedMedia?.type;
    if ((pageId === PageId.YOUTUBE && (!selectedMedia || isYoutubeShow(selectedMedia)))
      || (selectedMediaType === MediaType.MOVIE && pageId === PageId.MOVIES)
      || (selectedMediaType === MediaType.TVSHOW && pageId === PageId.SERIES)) {

      // if (!wordsData.words.length) {
      //   loadWords({ season: selectedSeason, episode: selectedEpisode });
      // }
      return;
    }

    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);

    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');
    setWordsLoading(false);
    setWordsChunkLoading(false);
  }, [pageId]);

  useEffect(() => {
    if (pageId !== PageId.YOUTUBE || !wordsLoading) {
      setYoutubeLoadingElapsedSec(0);
      return;
    }

    const timer = window.setInterval(() => {
      setYoutubeLoadingElapsedSec((prev) => prev + 1);
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [pageId, wordsLoading]);

  useEffect(() => {
    if (pageId === PageId.YOUTUBE || !wordsLoading) {
      setStandardLoadingElapsedSec(0);
      return;
    }

    const timer = window.setInterval(() => {
      setStandardLoadingElapsedSec((prev) => prev + 1);
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [pageId, wordsLoading]);

  useEffect(() => {
    if (loadMoreRef.current === wordsLoadMore) {
      return;
    }

    loadMoreRef.current = wordsLoadMore;

    if (wordsLoading || wordsChunkLoading || !selectedMedia) {
      return;
    }

    const nextPart = wordsData.nextPartToLoad || 0;
    if (nextPart < 1 || nextPart > wordsData.totalParts || wordsData.previewPending) {
      return;
    }

    loadMoreWords({ part: nextPart, media: selectedMedia });
  }, [wordsLoadMore, wordsLoading, wordsChunkLoading, selectedMedia, wordsData.nextPartToLoad, wordsData.totalParts, wordsData.previewPending]);

  useEffect(() => {
    if (wordToSaveRef.current === wordToSave) {
      return;
    }

    wordToSaveRef.current = wordToSave;

    if (!wordToSave) {
      return;
    }

    saveWord(wordToSave);
    setWordToSave(null);
    wordToSaveRef.current = null;
  }, [wordToSave]);

  useEffect(() => {
    if (!selectedMedia) {
      return;
    }

    // Do not auto-load while season/episode selector modal is open.
    // The user should explicitly confirm with "Get Important Words".
    if (mediaSelectorActive) {
      return;
    }

    if (wordsLoading) {
      return;
    }

    const selectedMediaType = selectedMedia.type;
    const compatible =
        (pageId === PageId.YOUTUBE && isYoutubeShow(selectedMedia))
        || (selectedMediaType === MediaType.MOVIE && pageId === PageId.MOVIES)
        || (selectedMediaType === MediaType.TVSHOW && pageId === PageId.SERIES);

    if (!compatible) {
      return;
    }

    // For TV shows wait until episode is selected, but clear old words immediately
    if (selectedMediaType === MediaType.TVSHOW && !selectedEpisode) {
      setWordsData(getWordsDataAtomDefaults());
      setWordsError('');
      return;
    }

    if (pageId === PageId.YOUTUBE) {
      if (!wordsData.words.length && selectedMedia.dbId && isYoutubeShow(selectedMedia)) {
        void (async () => {
          if (isLoadLockedForShow(selectedMedia)) {
            const canRestore = await canRestoreExistingProgressWhileLocked({
              media: selectedMedia,
              isYoutubeMode: true,
            });
            if (!canRestore) {
              return;
            }
          }
          const reqId = ++wordsReqIdRef.current;
          setWordsLoading(true);
          setYoutubeLoadingMode('restore');
          requestYoutubeWords({ screenmediaId: selectedMedia.dbId, part: 1 })
            .then((response) => {
              if (reqId !== wordsReqIdRef.current) {
                return;
              }
              if (!response.error && response.part === 1 && response.words.length) {
                if (response.mediaTitle || response.mediaPoster) {
                  setSelectedMedia((prev) => {
                    if (!prev) {
                      return prev;
                    }
                    return {
                      ...prev,
                      originalTitle: response.mediaTitle || prev.originalTitle,
                      poster: response.mediaPoster || prev.poster
                    };
                  });
                }
                setWordsData({
                  ...buildWordsState(response),
                });
                void hydratePreviewRemainder({
                  reqId,
                  response,
                  media: selectedMedia,
                  isYoutubeMode: true
                });
              }
            })
            .finally(() => {
              if (reqId === wordsReqIdRef.current) {
                setWordsLoading(false);
              }
            });
        })();
      }
      return;
    }

    loadWords({ season: selectedSeason, episode: selectedEpisode, trigger: 'auto' });
  }, [
    pageId,
    selectedMedia,
    selectedSeason?.num,
    selectedEpisode?.num,
    mediaSelectorActive,
    loadingShowIds,
  ]);

  // Extra chunks are loaded only by explicit user action ("Load more words").

  useEffect(() => {
    if (!forceSeasonPicker) {
      return;
    }
    if (pageId !== PageId.SERIES) {
      return;
    }
    if (!selectedMedia || selectedMedia.type !== MediaType.TVSHOW) {
      return;
    }
    if (!selectedMedia.seasons?.length) {
      return;
    }
    const forcedKey = String(
      (selectedMedia as any).dbId
      || (selectedMedia as any).imdbId
      || selectedMedia.title
      || ''
    );
    if (!forcedKey) {
      return;
    }
    if (forcedPickerOpenedRef.current === forcedKey) {
      return;
    }
    if (mediaSelectorActive) {
      return;
    }
    if (isLoadLockedForShow(selectedMedia)) {
      setWordsError(LOADING_LOCK_ERROR);
      return;
    }
    forcedPickerOpenedRef.current = forcedKey;
    setMediaSelectorActive(true);
  }, [forceSeasonPicker, pageId, selectedMedia, mediaSelectorActive, loadingShowIds]);

  if (pageId === PageId.YOUTUBE) {
    const estimatedProgress = Math.min(92, Math.round(6 + (youtubeLoadingElapsedSec * 86 / 90)));
    const youtubeUiMode: 'extract' | 'restore' = youtubeLoadingMode;

    return (<div className="search-panel">
      <div className="search-panel__hero">
        <div className="search-panel__hero-title">{sectionTitle}</div>
        <div className="search-panel__hero-hint">{sectionHint}</div>
      </div>
      <Form.Group className="autocomplete-group" controlId="formYoutubeUrl">
        <InputGroup>
          <InputGroup.Text className="uk-flag" />
        <Form.Control
            type="url"
            name="youtubeUrl"
            required
            placeholder="Paste a YouTube link..."
            autoComplete="off"
          disabled={wordsLoading || isGlobalLoadLockActive}
          value={youtubeUrl}
          onChange={(event) => setYoutubeUrl(event.target.value)}
        />
          <Button
            variant="outline-secondary"
            disabled={wordsLoading || !youtubeUrl.trim() || isLoadLockedForShow(parseYoutubeVideoId(youtubeUrl.trim()) || undefined)}
            onClick={loadYoutubeWords}
          >
            Get Words
          </Button>
        </InputGroup>
      </Form.Group>
      {isGlobalLoadLockActive && (
        <div className="loading-lock-note mb-2" role="status" aria-live="polite">
          <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          <div className="loading-lock-note__content">
            <div className="loading-lock-note__title">
              Loading words for {loadingItemTitle}{loadingItemEpisodeTag ? ` (${loadingItemEpisodeTag})` : ''}
            </div>
            <div className="loading-lock-note__text">New extraction is temporarily locked until this loading finishes.</div>
          </div>
          {!!activeLoadingItem?.show && (
            <Button variant="link" size="sm" className="loading-lock-note__link" onClick={openCurrentLoadingItem}>
              Open
            </Button>
          )}
        </div>
      )}
      {selectedMedia && isYoutubeShow(selectedMedia) && (
        <Button variant="outline-secondary" className="d-flex w-100 mb-2" disabled>
          <MediaInfo media={selectedMedia} />
        </Button>
      )}
      {wordsLoading && (
        <div className="youtube-loading-panel border rounded p-3 mt-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
            <strong>{getLoadingTitle(youtubeUiMode)}</strong>
          </div>
          <div className="progress youtube-loading-progress mb-2" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={estimatedProgress}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              style={{ width: `${estimatedProgress}%` }}
            />
          </div>
          <small className="text-muted">{getLoadingEta(youtubeUiMode, youtubeLoadingElapsedSec)}</small>
          <div className="text-muted mt-1">{getLoadingHint(youtubeLoadingElapsedSec)}</div>
        </div>
      )}
    </div>);
  }

  return (<div className="search-panel">
    <div className="search-panel__hero">
      <div className="search-panel__hero-title">{sectionTitle}</div>
      <div className="search-panel__hero-hint">{sectionHint}</div>
    </div>
    {/* Show Name Input with Autocomplete */}
    <Form.Group className="autocomplete-group" controlId="formShowName">
      <InputGroup>
        <InputGroup.Text className="uk-flag"/>
        <Form.Control
          type="text"
          name="showName"
          required
          placeholder={searchPlaceholder}
          autoComplete="off"
          ref={showNameRef}
          disabled={wordsLoading || isGlobalLoadLockActive}
          onChange={(event) => {
            const query = event.target.value.trim();
            if (query && (selectedMedia || selectedSeason || selectedEpisode || wordsData.words.length)) {
              resetSelectedContext();
            }
            if (query.length) {
              setAutocompleteLoading(true);
            } else {
              setAutocompleteLoading(false);
              if (autocomplete.length) {
                setAutocomplete([]);
              }
            }
            handleSearchChange(query);
          }}
        />
      </InputGroup>
      {autocompleteLoading && (<Spinner
        className="autocomplete-spinner"
        animation="border"
        size="sm"
        role="status"
        aria-hidden="true"
        variant="secondary"
      />)}
      {!!autocomplete.length && (<AutocompleteDropdown autocomplete={autocomplete} onSelect={handleSearchSelect} />)}
    </Form.Group>
    {isGlobalLoadLockActive && (
      <div className="loading-lock-note mb-2" role="status" aria-live="polite">
        <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
        <div className="loading-lock-note__content">
          <div className="loading-lock-note__title">
            Loading words for {loadingItemTitle}{loadingItemEpisodeTag ? ` (${loadingItemEpisodeTag})` : ''}
          </div>
          <div className="loading-lock-note__text">New extraction is temporarily locked until this loading finishes.</div>
        </div>
        {!!activeLoadingItem?.show && (
          <Button variant="link" size="sm" className="loading-lock-note__link" onClick={openCurrentLoadingItem}>
            Open
          </Button>
        )}
      </div>
    )}
    {selectedMedia && (<Button
      variant="outline-secondary"
      className="d-flex w-100 mb-1"
      onClick={handleMediaSelectorOpenSafe}
      disabled={isGlobalLoadLockActive}
      title={isGlobalLoadLockActive ? LOADING_LOCK_ERROR : undefined}
    >
      <MediaInfo media={selectedMedia} season={selectedSeason} episode={selectedEpisode} />
      <div><i className="ic ic-se-selector" /></div>
    </Button>)}
    {wordsLoading && selectedMedia && !mediaSelectorActive && standardLoadingMode !== 'load_more' && (
      <div className="standard-loading-panel border rounded p-3 mt-2">
        {(() => {
          const estimatedProgress = effectiveStandardLoadingMode === 'extract'
            ? Math.min(92, Math.round(8 + (standardLoadingElapsedSec * 84 / 55)))
            : effectiveStandardLoadingMode === 'restore'
            ? Math.min(95, Math.round(18 + (standardLoadingElapsedSec * 78 / 16)))
            : Math.min(95, Math.round(20 + (standardLoadingElapsedSec * 75 / 18)));
          if (effectiveStandardLoadingMode === 'restore') {
            return (
              <div className="standard-restore-preview" aria-hidden="true">
                <div className="standard-restore-preview__header">
                  <div className="standard-restore-preview__title skeleton-block skeleton-block--title" />
                  <div className="standard-restore-preview__subtitle skeleton-block skeleton-block--subtitle" />
                </div>
                <div className="standard-restore-preview__chips">
                  <span className="standard-restore-preview__chip skeleton-block skeleton-block--chip" />
                  <span className="standard-restore-preview__chip skeleton-block skeleton-block--chip" />
                </div>
                <div className="standard-restore-preview__row">
                  <div className="standard-restore-preview__donut skeleton-block" />
                  <div className="standard-restore-preview__legend">
                    <div className="standard-restore-preview__legend-item">
                      <span className="dot skeleton-block" />
                      <span className="skeleton-block skeleton-block--legend-label" />
                      <strong className="skeleton-block skeleton-block--legend-value" />
                    </div>
                    <div className="standard-restore-preview__legend-item">
                      <span className="dot skeleton-block" />
                      <span className="skeleton-block skeleton-block--legend-label" />
                      <strong className="skeleton-block skeleton-block--legend-value" />
                    </div>
                    <div className="standard-restore-preview__legend-item">
                      <span className="dot skeleton-block" />
                      <span className="skeleton-block skeleton-block--legend-label" />
                      <strong className="skeleton-block skeleton-block--legend-value" />
                    </div>
                  </div>
                </div>
                <div className="standard-restore-preview__note">
                  <div className="skeleton-block skeleton-block--note-lg" />
                  <div className="skeleton-block skeleton-block--note-sm" />
                </div>
              </div>
            );
          }
          return (
            <>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                <strong>{getLoadingTitle(effectiveStandardLoadingMode)}</strong>
              </div>
              <div className="progress standard-loading-progress mb-2" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={estimatedProgress}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${estimatedProgress}%` }}
                />
              </div>
              <small className="text-muted">{getLoadingEta(effectiveStandardLoadingMode, standardLoadingElapsedSec)}</small>
              <div className="text-muted mt-1">{getLoadingHint(standardLoadingElapsedSec)}</div>
            </>
          );
        })()}
      </div>
    )}
    {(mediaSelectorActive && selectedMedia) && (<MediaSelector onExit={handleMediaSelectorExit} onSubmit={loadWords} />)}
  </div>);
};

export default SearchPanel;
